import type { ToolContent } from "./tool-content";

const genericFrContent = (name: string, action: string): ToolContent => ({
  seoTitle: `${name} Gratuit en Ligne — Outil Rapide & Équitable | Picamini.online`,
  seoDescription: `Utilisez ${name.toLowerCase()} gratuitement en ligne. ${action} 100% gratuit, sans inscription, sans publicité intrusive. Idéal pour enseignants, élèves, réunions et événements.`,
  h1Title: `${name} Gratuit en Ligne — Simple, Rapide et 100% dans le Navigateur`,
  intro: `${action} Spécialement conçu pour les enseignants, étudiants, animateurs et équipes. Tout fonctionne localement et en toute confidentialité dans votre navigateur sans création de compte obligatoire.`,
  whatIs: [
    `L'outil ${name} est une application web gratuite et polyvalente conçue pour rendre vos tirages au sort, choix d'élèves et répartitions de groupes rapides, équitables et visuellement captivants.`,
    `Que vous prépariez un cours, animiez une réunion d'équipe ou organisiez un jeu en groupe, cette application remplace les tirages papier manuels par une sélection aléatoire fluide, dotée d'une interface claire et d'effets sonores stimulants.`,
    `Contrairement aux sites complexes, notre outil ${name.toLowerCase()} ne nécessite aucune inscription, aucune installation de logiciel et ne collecte aucune donnée personnelle. Ouvrez la page et commencez immédiatement.`
  ],
  howTo: [
    `Ouvrez l'outil ${name} sur votre ordinateur, tablette ou smartphone.`,
    "Saisissez votre liste de prénoms, vos participants ou vos chiffres dans le champ dédié.",
    "Ajustez vos préférences (effets sonores, nombre de gagnants ou options de récurrence).",
    "Cliquez sur le bouton principal pour lancer le tirage au sort équitable.",
    "Suivez l'animation fluide et écoutez le son de révélation jusqu'au résultat final.",
    "Utilisez le bouton de copie ou d'impression pour conserver et partager les résultats."
  ],
  features: [
    "100% Gratuit avec utilisation illimitée sans restriction quotidienne",
    "Aucune inscription ni adresse e-mail requise",
    "Traitement 100% local dans le navigateur — vos listes ne quittent jamais votre appareil",
    "Optimisé pour la projection sur TBI, TICE, vidéoprojecteur et tableau interactif",
    "Effets sonores stimulants avec bouton de désactivation rapide",
    "Commande au clavier accessible (Barre d'espace pour démarrer, Échap pour plein écran)",
    "Entièrement adaptatif sur smartphone, iPad, tablette Android et ordinateur",
    "Options de copie instantanée et d'impression pour la classe"
  ],
  benefits: [
    "Gagne un temps précieux en cours par rapport au tirage papier traditionnel",
    "Garantit une égalité des chances parfaite et évite tout sentiment de partialité",
    "Capte l'attention et stimule la motivation grâce aux animations dynamiques",
    "Fonctionne même avec une connexion Internet faible une fois la page chargée",
    "Interface fluide et claire sans fenêtres intempestives ni publicités parasites"
  ],
  proTips: [
    "Projetez l'outil directement au tableau pour que tous les élèves suivent le tirage en direct.",
    "Activez l'option 'Retirer le prénom tiré' afin de garantir que chaque élève passe une fois.",
    "Enregistrez votre liste personnalisée dans les favoris de votre navigateur : elle reste conservée localement.",
    "Utilisez la fonction d'impression pour les cahiers de texte et récapitulatifs de cours."
  ],
  useCases: [
    {
      role: "Enseignants & Professeurs (Maternelle, Élémentaire, Collège, Lycée)",
      description: "Interrogez les élèves de manière équitable, formez des groupes équilibrés, organisez les plans de classe et gérez le temps avec le minuteur."
    },
    {
      role: "Animateurs, Formateurs & Organisateurs d'Événements",
      description: "Dynamisez vos réunions, ateliers et soirées jeux avec la roue de décision, le tirage de cadeaux et les compteurs de points."
    }
  ],
  detailedGuide: [
    {
      heading: `Guide complet d'utilisation de l'outil ${name}`,
      paragraphs: [
        `Dans les environnements d'apprentissage et de travail modernes, la transparence et l'équité sont essentielles pour maintenir l'engagement de tous. L'outil ${name.toLowerCase()} offre une solution numérique fiable qui élimine tout biais humain lors des décisions quotidiennes.`,
        `Notre application s'appuie sur des algorithmes aléatoires sécurisés s'exécutant directement dans le navigateur de l'utilisateur. Ainsi, chaque option saisie bénéficie exactement de la même probabilité d'être sélectionnée.`
      ],
      bulletPoints: [
        "Résultats instantanés à l'écran sans délai d'attente",
        "Protection absolue des données : les prénoms restent sur votre appareil",
        "Compatible avec tous les systèmes (Windows, Mac, iOS, Android, Chromebook)"
      ]
    }
  ],
  comparisonTable: {
    title: "Comparatif : Picamini.online vs Méthodes Traditionnelles",
    headers: ["Critère", "Picamini.online", "Tirage Papier Classique"],
    rows: [
      ["Rapidité de préparation", "Instantanée (Copier-Coller)", "Lente (Découpage de papiers)"],
      ["Équité et Impartialité", "Algorithme mathématique neutre", "Risque d'erreur ou de choix biaisé"],
      ["Impact visuel & Motivation", "Animations et sons stimulants", "Effet neutre"],
      ["Re-utilisation des listes", "Sauvegarde automatique locale", "Papiers perdus ou réécritures"]
    ]
  },
  faq: [
    {
      q: `L'outil ${name} est-il vraiment gratuit ?`,
      a: "Oui, Picamini.online est 100% gratuit, sans abonnement, sans limitation et sans frais cachés."
    },
    {
      q: "Mes données ou listes de prénoms sont-elles enregistrées sur un serveur ?",
      a: "Non. Vos listes de prénoms et vos configurations restent exclusivement stockées dans le navigateur de votre appareil."
    },
    {
      q: "Puis-je utiliser cet outil sur un tableau blanc interactif (TBI/TICE) ?",
      a: "Absolument. L'interface a été conçue pour s'afficher en grand écran et fonctionner avec les écrans tactiles et projecteurs de classe."
    }
  ]
});

export function getFrToolContent(slug: string): ToolContent {
  switch (slug) {
    case "tirage-au-sort-prenoms":
    case "random-name-picker":
      return {
        seoTitle: "Tirage au Sort de Prénoms en Ligne Gratuit | Picamini.online",
        seoDescription: "Faites un tirage au sort de prénoms facile, rapide et gratuit. Idéal pour les professeurs en classe, les tirages au sort de cadeaux et les réunions d'équipe.",
        h1Title: "Tirage au Sort de Prénoms en Ligne — Équitable, Animé & Gratuit",
        intro: "Sélectionnez un nom ou un participant au hasard de manière parfaitement équitable. Insérez votre liste, cliquez et laissez l'animation désigner le gagnant !",
        whatIs: [
          "Le Tirage au Sort de Prénoms en Ligne est un outil numérique intuitif conçu pour sélectionner aléatoirement un ou plusieurs prénoms parmi une liste personnalisée.",
          "Conçu à l'origine pour aider les enseignants à interroger les élèves sans favoritisme, il est également très apprécié lors des tirages au sort de jeux concours, des Secret Santa et des activités d'animation.",
          "Grâce à son algorithme impartial et son affichage fluide grand format, cet outil rend chaque tirage amusant et transparent."
        ],
        howTo: [
          "Collez ou saisissez votre liste de prénoms dans la zone de texte.",
          "Indiquez le nombre de personnes à tirer au sort.",
          "Cochez la case 'Retirer le prénom tiré' si vous souhaitez éviter les doublons.",
          "Cliquez sur le bouton 'Tirer au sort' pour lancer le résultat.",
          "Copiez ou imprimez la liste des gagnants retenus."
        ],
        features: [
          "Animation dynamique avec effets sonores optionnels",
          "Gestion des tirages multiples en une seule fois",
          "Sauvegarde automatique de votre liste sur votre appareil",
          "Mode plein écran adapté aux vidéoprojecteurs scolaires",
          "Exportation rapide des résultats en un clic"
        ],
        benefits: [
          "Élimine tout soupçon de favoritisme dans la classe",
          "Offre un moment ludique et captivant à vos élèves",
          "Permet de réutiliser la même liste tous les jours sans effort"
        ],
        proTips: [
          "Ajoutez un symbole ou un numéro devant les prénoms pour repérer facilement vos groupes.",
          "Désactivez le son d'un clic sur l'icône de haut-parleur si vous préférez un tirage silencieux."
        ],
        useCases: [
          { role: "Enseignants", description: "Interrogez un élève au hasard ou attribuez les prises de parole de façon impartiale." },
          { role: "Organisateurs", description: "Désignez le gagnant d'une tombola, d'un jeu concours ou d'un tirage au sort." }
        ],
        detailedGuide: [
          {
            heading: "Pourquoi adopter un tirage au sort de prénoms numérique ?",
            paragraphs: [
              "Le tirage papier traditionnel demande du temps et comporte parfois des biais d'organisation. En utilisant notre sélecteur de prénoms numérique, vous bénéficiez d'une solution instantanée, visuelle et irréprochable sur le plan de la neutralité.",
              "Que ce soit sur ordinateur, tablette ou smartphone, l'application garantit une expérience fluide et respectueuse de la vie privée."
            ],
            bulletPoints: [
              "Aucun compte à créer",
              "Utilisation illimitée et gratuite",
              "Données conservées uniquement dans votre navigateur"
            ]
          }
        ],
        comparisonTable: {
          title: "Pourquoi choisir le Tirage au Sort de Prénoms Picamini ?",
          headers: ["Fonctionnalité", "Picamini.online", "Autres outils"],
          rows: [
            ["Publicité", "Aucune pub intrusive", "Nombreuses pubs distrayantes"],
            ["Confidentialité", "Stockage local à 100%", "Envoi des données vers des serveurs"],
            ["Compatibilité TBI", "Optimisé grand écran", "Souvent mal adapté aux projecteurs"]
          ]
        },
        faq: [
          { q: "Puis-je enregistrer ma liste d'élèves ?", a: "Oui, la liste reste sauvegardée automatiquement dans le stockage local de votre navigateur." },
          { q: "Est-ce gratuit ?", a: "Oui, l'outil est 100% gratuit et illimité." }
        ]
      };

    case "roue-de-la-fortune-en-ligne":
    case "spin-the-wheel":
      return {
        seoTitle: "Roue de la Fortune en Ligne & Roue de Décision Gratuit | Picamini.online",
        seoDescription: "Faites tourner la roue de la fortune en ligne interactive. Personnalisez vos options, lancez la roue et prenez des décisions rapides et amusantes.",
        h1Title: "Roue de la Fortune en Ligne — Tourner la Roue de Décision",
        intro: "Besoin de trancher un choix ou d'animer un jeu ? Personnalisez les tranches de la roue colorée et faites-la tourner d'un simple clic !",
        whatIs: [
          "La Roue de la Fortune en Ligne est un générateur visuel de tirage au sort sous forme de roue à tourner.",
          "Elle permet d'inscrire vos propres choix, prénoms, questions ou récompenses sur des segments colorés et de faire tourner l'aiguille de façon réaliste.",
          "C'est l'outil parfait pour dynamiser un cours, organiser un tirage au sort ou prendre une décision en groupe."
        ],
        howTo: [
          "Entrez vos options dans le champ de saisie (une option par ligne).",
          "Cliquez sur le bouton 'Faire Tourner' pour lancer la roue.",
          "Attendez que la roue ralentisse et s'arrête sur la tranche gagnante.",
          "Découvrez l'option tirée au sort avec l'animation de célébration."
        ],
        features: [
          "Graphismes colorés et lisibles à distance",
          "Physique de rotation fluide et réaliste",
          "Possibilité de retirer l'élément gagnant après le tirage",
          "Format totalement adapté aux smartphones et écrans interactifs"
        ],
        benefits: [
          "Crée du suspense et de la convivialité lors des tirages",
          "Aide les personnes indécises à prendre une décision rapidement",
          "Très visuel pour les enfants et les présentations de groupe"
        ],
        proTips: [
          "Utilisez la roue pour choisir le thème du jour ou le sujet d'un exercice.",
          "Ajustez la durée de rotation dans les options pour créer plus de suspense."
        ],
        useCases: [
          { role: "Profs & Éducateurs", description: "Tirage au sort de questions de révision, récompenses ou élèves interrogés." },
          { role: "Groupes & Familles", description: "Choix du film du soir, du restaurant ou de l'activité du week-end." }
        ],
        detailedGuide: [
          {
            heading: "Comment utiliser la roue de décision dans vos animations ?",
            paragraphs: [
              "La roue de la fortune apporte un aspect ludique inégalé. Sa rotation crée une attente visuelle enthousiasmante qui capte l'attention des participants.",
              "Elle est idéale aussi bien en classe virtuelle qu'en présentiel sur un grand écran."
            ],
            bulletPoints: [
              "Inscriptions 100% éditables",
              "Animation fluide en haute définition",
              "Sans inscription obligatoire"
            ]
          }
        ],
        comparisonTable: {
          title: "Avantages de la Roue Interactive Picamini",
          headers: ["Caractéristique", "Notre Roue", "Roues classiques"],
          rows: [
            ["Vitesse de chargement", "Instantannée", "Lente avec des scripts de pub"],
            ["Utilisation sur mobile", "Parfaitement responsive", "Difficile à faire tourner au doigt"]
          ]
        },
        faq: [
          { q: "Combien d'options puis-je ajouter sur la roue ?", a: "Vous pouvez ajouter de 2 à plusieurs dizaines d'options sur la roue." },
          { q: "La roue est-elle vraiment aléatoire ?", a: "Oui, la rotation et le point d'arrêt sont générés par un algorithme aléatoire neutre." }
        ]
      };

    default:
      return genericFrContent(slug.replace(/-/g, " "), "Outil de décision et tirage au sort gratuit.");
  }
}
